// Node Palette Sidebar for Visual Flow Builder (PRD §1.9, WP-08).
// 17 draggable node blocks categorized into Messages, Data Collection, Interactive, Flow Control, and Integrations.
import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  NODE_CATALOGUE,
  type FlowNodeType,
  type NodeCategory,
} from '../../lib/flows';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon } from '../icons';

interface NodePaletteProps {
  onAddNode: (type: FlowNodeType) => void;
  className?: string;
}

const CATEGORIES: { id: NodeCategory; titleKey: string; defaultTitle: string }[] = [
  { id: 'messages', titleKey: 'flow.category.messages', defaultTitle: 'Messages' },
  { id: 'data', titleKey: 'flow.category.data', defaultTitle: 'Data Collection' },
  { id: 'interactive', titleKey: 'flow.category.interactive', defaultTitle: 'Interactive' },
  { id: 'control', titleKey: 'flow.category.control', defaultTitle: 'Flow Control' },
  { id: 'integrations', titleKey: 'flow.category.integrations', defaultTitle: 'Integrations' },
];

export default function NodePalette({ onAddNode, className }: NodePaletteProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const onDragStart = (event: React.DragEvent, nodeType: FlowNodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const filteredCatalogue = NODE_CATALOGUE.filter((item) => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    const title = (t(item.labelKey) || item.defaultLabel).toLowerCase();
    const desc = (t(item.descKey) || item.defaultDesc).toLowerCase();
    return title.includes(query) || desc.includes(query) || item.type.includes(query);
  });

  return (
    <div
      className={cn(
        'flex flex-col h-full border-e border-line bg-white dark:border-slate-800 dark:bg-slate-900 overflow-hidden',
        className
      )}
    >
      {/* Palette Header */}
      <div className="p-3.5 border-b border-line dark:border-slate-800">
        <h3 className="text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400 mb-2">
          {t('flow.palette.title') || 'Block Palette (17 Nodes)'}
        </h3>
        <div className="relative">
          <SearchIcon className="absolute start-2.5 top-2.5 h-3.5 w-3.5 text-ink-4 dark:text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('flow.palette.searchPlaceholder') || 'Search blocks...'}
            className="w-full rounded-xl border border-line bg-surface/50 ps-8 pe-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          />
        </div>
      </div>

      {/* Palette Items List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4 text-xs">
        {CATEGORIES.map((cat) => {
          const items = filteredCatalogue.filter((item) => item.category === cat.id);
          if (items.length === 0) return null;

          const isCollapsed = collapsedCategories[cat.id];

          return (
            <div key={cat.id} className="space-y-1.5">
              <button
                type="button"
                onClick={() => toggleCategory(cat.id)}
                className="flex w-full items-center justify-between py-1 text-xs font-bold text-ink-2 dark:text-slate-300 hover:text-ink transition-colors"
              >
                <span>{t(cat.titleKey) || cat.defaultTitle}</span>
                {isCollapsed ? (
                  <ChevronDownIcon className="h-3.5 w-3.5 text-ink-4" />
                ) : (
                  <ChevronUpIcon className="h-3.5 w-3.5 text-ink-4" />
                )}
              </button>

              {!isCollapsed && (
                <div className="space-y-1.5">
                  {items.map((item) => {
                    const title = t(item.labelKey) || item.defaultLabel;
                    const desc = t(item.descKey) || item.defaultDesc;

                    return (
                      <div
                        key={item.type}
                        draggable
                        onDragStart={(e) => onDragStart(e, item.type)}
                        className="group relative flex items-center justify-between rounded-xl border border-line bg-surface/30 p-2.5 cursor-grab active:cursor-grabbing hover:border-brand hover:bg-brand/5 dark:border-slate-800 dark:bg-slate-800/40 dark:hover:border-brand dark:hover:bg-brand/10 transition-all"
                      >
                        <div className="flex items-center gap-2.5 min-w-0 pr-2">
                          <span className="text-lg flex-shrink-0" aria-hidden>
                            {item.icon}
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-ink dark:text-white truncate">
                                {title}
                              </span>
                              {item.badge && (
                                <span className="rounded bg-brand/10 px-1 py-0.2 text-[9px] font-bold text-brand">
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-ink-3 dark:text-slate-400 line-clamp-1">
                              {desc}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => onAddNode(item.type)}
                          title="Add to canvas"
                          className="rounded-lg p-1 text-ink-3 opacity-0 group-hover:opacity-100 hover:bg-brand/10 hover:text-brand transition-all flex-shrink-0"
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
